<template>
  <Teleport to="body">
    <div
      class="modal fade"
      :id="modal_id"
      tabindex="-1"
      aria-labelledby="exportModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered modal-lg export-modal-dialog">
        <div class="modal-content border-0 shadow-lg">
          <!-- Header -->
          <div class="modal-header border-0">
            <div class="d-flex align-items-center">
              <div class="export-header-icon me-3">
                <i class="bi bi-box-arrow-up-right"></i>
              </div>
              <div>
                <h5 class="modal-title fw-bold mb-0" id="exportModalLabel">
                  Export Report Data
                </h5>
                <small class="text-white-50">
                  Choose format &amp; sections to export
                </small>
              </div>
            </div>
            <button
              type="button"
              class="btn-close btn-close-white"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>

          <!-- Body -->
          <div class="modal-body">
            <!-- Export Format Selection -->
            <div class="mb-4">
              <label class="form-label fw-semibold text-sm text-muted mb-2">
                <i class="bi bi-file-earmark me-1"></i>
                Export Format
              </label>
              <div class="row g-2">
                <div class="col-4" v-for="fmt in format_options" :key="fmt.value">
                  <input
                    type="radio"
                    class="btn-check"
                    :name="`exportFormat-${modal_id}`"
                    :id="`format-${fmt.value}-${modal_id}`"
                    :value="fmt.value"
                    v-model="export_format"
                    autocomplete="off"
                  />
                  <label
                    class="btn btn-outline-success format-card w-100 d-flex flex-column align-items-center py-3"
                    :for="`format-${fmt.value}-${modal_id}`"
                  >
                    <i :class="fmt.icon" class="fs-4 mb-1"></i>
                    <span class="fw-semibold">{{ fmt.label }}</span>
                    <small class="text-muted format-desc">{{ fmt.desc }}</small>
                  </label>
                </div>
              </div>
            </div>

            <!-- Divider -->
            <hr class="my-3 opacity-10" />

            <!-- Data Sections Selection -->
            <div class="mb-3">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <label class="form-label fw-semibold text-sm text-muted mb-0">
                  <i class="bi bi-list-check me-1"></i>
                  Select Data to Export
                </label>
                <button
                  type="button"
                  class="btn btn-link btn-sm p-0 text-decoration-none"
                  @click="toggle_select_all"
                  style="color: var(--primary-color);"
                >
                  {{ all_selected ? 'Deselect All' : 'Select All' }}
                </button>
              </div>
              <div class="export-options-container">
                <div
                  v-for="(label, key) in available_sections"
                  :key="key"
                  class="export-option-item"
                  :class="{ 'active': export_options[key] }"
                  @click="export_options[key] = !export_options[key]"
                >
                  <div class="form-check mb-0">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :id="`export-${key}-${modal_id}`"
                      v-model="export_options[key]"
                      @click.stop
                    />
                    <label
                      class="form-check-label text-sm w-100"
                      :for="`export-${key}-${modal_id}`"
                      @click.stop
                    >
                      <i :class="section_icon(key)" class="me-2 text-muted"></i>
                      {{ label }}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Selected Count Badge -->
            <div class="d-flex align-items-center justify-content-between">
              <div v-if="has_selected_sections">
                <span class="badge bg-success-subtle rounded-pill px-3 py-2" style="color: var(--primary-color);">
                  <i class="bi bi-check2-circle me-1"></i>
                  {{ selected_count }} section{{ selected_count !== 1 ? 's' : '' }} selected
                </span>
              </div>
              <div v-else class="alert alert-warning py-2 px-3 mb-0 w-100 d-flex align-items-center">
                <i class="bi bi-exclamation-triangle me-2"></i>
                <small>Please select at least one data section to export.</small>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer border-0">
            <button
              type="button"
              class="btn btn-bg-green text-xs rounded-small shadow-sm px-4"
              data-bs-dismiss="modal"
              :disabled="is_exporting"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-bg-green text-xs rounded-small shadow-sm px-4 d-flex align-items-center"
              @click="handle_export"
              :disabled="!has_selected_sections || is_exporting"
            >
              <span
                v-if="is_exporting"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              <i v-else class="bi bi-download me-2"></i>
              {{ is_exporting ? 'Exporting...' : 'Export Data' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { export_service_factory } from './export_service_factory';

export default {
  name: 'ExportDataModal',
  props: {
    /**
     * Unique identifier for this modal instance
     */
    modal_id: {
      type: String,
      required: true,
    },
    /**
     * Service type for data export (e.g., 'aml', 'ocr_liveness')
     */
    service_type: {
      type: String,
      required: true,
      validator: (value) =>
        ['aml', 'ocr_liveness', 'individual_liveness', 'comprehensive_liveness',
         'rsa_id_verification', 'ocr_verification', 'id_liveness',
         'enterprise_aml', 'enterprise_credit', 'huru_criminal_check'].includes(value),
    },
    /**
     * The raw data object to export
     */
    data: {
      type: Object,
      required: true,
    },
    /**
     * Additional metadata (sheet_elements, response_info, etc.)
     */
    metadata: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      export_format: 'csv',
      export_options: {},
      export_service: null,
      is_exporting: false,
      format_options: [
        {
          value: 'csv',
          label: 'CSV',
          icon: 'bi bi-filetype-csv',
          desc: 'Spreadsheet',
        },
        {
          value: 'excel',
          label: 'Excel',
          icon: 'bi bi-file-earmark-excel',
          desc: 'Workbook',
        },
        {
          value: 'json',
          label: 'JSON',
          icon: 'bi bi-filetype-json',
          desc: 'Raw data',
        },
      ],
    };
  },
  computed: {
    /**
     * Available sections based on the service type.
     * raw_json is only shown when the JSON format is selected.
     */
    available_sections() {
      const sections = this.export_service?.get_available_sections() || {};
      if (this.export_format !== 'json') {
        return Object.fromEntries(Object.entries(sections).filter(([key]) => key !== 'raw_json'));
      }
      return sections;
    },
    /**
     * Check if at least one section is selected
     */
    has_selected_sections() {
      return Object.values(this.export_options).some((selected) => selected);
    },
    /**
     * Count of selected sections
     */
    selected_count() {
      return Object.values(this.export_options).filter(Boolean).length;
    },
    /**
     * Check if all sections are selected
     */
    all_selected() {
      const values = Object.values(this.export_options);
      return values.length > 0 && values.every(Boolean);
    },
  },
  watch: {
    service_type: {
      immediate: true,
      handler(new_type) {
        this.export_service = export_service_factory(new_type, this.data, this.metadata);
        this.export_options = this.export_service.get_default_export_options();
      },
    },
    export_format(new_format) {
      if (new_format !== 'json' && 'raw_json' in this.export_options) {
        this.export_options.raw_json = false;
      }
    },
  },
  methods: {
    /**
     * Toggle select all / deselect all
     */
    toggle_select_all() {
      const new_value = !this.all_selected;
      Object.keys(this.export_options).forEach((key) => {
        this.export_options[key] = new_value;
      });
    },
    /**
     * Get icon class for a section key
     */
    section_icon(key) {
      const icons = {
        subject_information: 'bi bi-person-badge',
        subject_info: 'bi bi-person-badge',
        screening_summary: 'bi bi-shield-check',
        negative_media: 'bi bi-newspaper',
        pep: 'bi bi-people',
        watchlist: 'bi bi-exclamation-diamond',
        raw_json: 'bi bi-code-slash',
        personal_information: 'bi bi-person',
        document_details: 'bi bi-file-earmark-text',
        facial_comparison_liveness: 'bi bi-camera',
        document_authentication: 'bi bi-patch-check',
        order_summary: 'bi bi-receipt',
        search_results: 'bi bi-search',
        order_profiles: 'bi bi-person-badge',
        result_items: 'bi bi-clipboard-check',
        payment_details: 'bi bi-credit-card',
        meta_data: 'bi bi-info-circle',
      };
      return icons[key] || 'bi bi-check2-square';
    },
    /**
     * Handle export button click
     */
    async handle_export() {
      if (!this.has_selected_sections) return;

      this.is_exporting = true;
      try {
        await this.export_service.export_data(
          this.export_format,
          this.export_options
        );
        this.close_modal();
      } catch (error) {
        console.error('Export failed:', error);
        alert(`Export failed: ${error.message}`);
      } finally {
        this.is_exporting = false;
      }
    },
    /**
     * Close the modal
     */
    close_modal() {
      $(`#${this.modal_id}`).modal('hide');
    },
    /**
     * Open the modal (called from parent)
     */
    open_modal() {
      $(`#${this.modal_id}`).modal('show');
    },
  },
};
</script>

<style scoped>
.export-modal-dialog {
  max-width: 920px;
}

.modal-content {
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #e9ecef !important;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.18) !important;
}

.modal-header {
  background: var(--primary-color, #059669);
  color: var(--client-text);
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
}

.modal-header .btn-close {
  filter: brightness(0) invert(1);
  opacity: 0.7;
}

.modal-header .btn-close:hover {
  opacity: 1;
}

.export-header-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.22);
}

.modal-body {
  padding: 1.5rem 1.6rem;
  background: #ffffff;
}

.modal-footer {
  padding: 0.95rem 1.6rem;
  background-color: #f8fafc;
  border-top: 1px solid #eef2f7;
}

/* Format Cards */
.format-card {
  border-radius: 10px;
  border: 1px solid #dde3ea;
  transition: all 0.2s ease;
  cursor: pointer;
  min-height: 96px;
}

.format-card:hover {
   border-color: var(--primary-color, #9dd7bd);
   background-color: #f7fbf9;
   color: var(--primary-color, #047857);
}

.format-desc {
  font-size: 0.7rem;
  line-height: 1;
  margin-top: 2px;
}

.btn-check:checked + .format-card {
   background-color: var(--primary-color, #065f46);
   border-color: var(--primary-color, #065f46);
   color: white;
   box-shadow: 0 8px 18px rgba(2, 6, 23, 0.18);
}

.btn-check:checked + .format-card .text-muted {
  color: rgba(255, 255, 255, 0.7) !important;
}

/* Export Options */
.export-options-container {
  max-height: 290px;
  overflow-y: auto;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #fcfdff;
}

.export-option-item {
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #eef2f7;
  cursor: pointer;
  transition: all 0.15s ease;
}

.export-option-item:last-child {
  border-bottom: none;
}

.export-option-item:hover {
  background-color: #f8fbf9;
}

.export-option-item.active {
  background-color: #edf9f3;
   border-left: 3px solid var(--primary-color, #059669);
}

.form-check {
  padding-left: 1.75rem;
}

.form-check-input {
  border-radius: 4px;
}

.form-check-input:checked {
  background-color: var(--primary-color, #10b981);
  border-color: var(--primary-color, #10b981);
}

.form-check-input:focus {
  border-color: var(--primary-color, #10b981);
  box-shadow: 0 0 0 0.2rem rgba(2, 6, 23, 0.06);
}

.text-sm {
  font-size: 0.875rem;
}

.alert-warning {
  background-color: #fff8e8;
  border: 1px solid #f7dca5;
  border-radius: 8px;
  font-size: 0.85rem;
  color: #92400e;
}

.badge.bg-success-subtle {
  background-color: #ecfdf5 !important;
  font-weight: 500;
  font-size: 0.8rem;
}

.btn-success {
   background-color: var(--primary-color, #047857);
   border-color: var(--primary-color, #047857);
   color: var(--primary-contrast, #ffffff);
 }
 
 .btn-success:hover:not(:disabled) {
   filter: brightness(0.92);
   box-shadow: 0 6px 14px rgba(2, 6, 23, 0.08);
}

.export-options-container::-webkit-scrollbar-track {
  background: transparent;
}

.export-options-container::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 4px;
}

.export-options-container::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

@media (max-width: 992px) {
  .export-modal-dialog {
    max-width: calc(100% - 1.25rem);
  }
}

@media (max-width: 576px) {
  .modal-body,
  .modal-footer,
  .modal-header {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .format-card {
    min-height: 88px;
    padding-top: 0.6rem !important;
    padding-bottom: 0.6rem !important;
  }
}
</style>
